/**
 * A trading gateway = an opaque unit of name + config (SignalR hub URL, servers,
 * accepting flag), advertised by the CRM (`GET /api/gateways`). The terminal never
 * knows or cares what provider runs behind a gateway — the gateway deployable owns
 * its own protocol internals.
 */

export interface GatewayServer {
  /** Broker-server display name, e.g. "ICMarkets-Live". */
  name: string;
  /** Broker/engine host. */
  host: string;
  /** Broker/engine port. */
  port: number;
  accountsHint?: number | null;
}

export interface Gateway {
  slug: string;
  name: string;
  /** Reachable SignalR base URL for this gateway (dev → http://localhost:5001|5011). */
  hubUrl: string;
  /**
   * Every address the CRM holds for this card, in the operator's order (one per gateway instance of
   * the broker's stack). `hubUrl` is the one that answered the CRM just now. Absent from an older CRM.
   */
  hubUrls?: string[];
  accepting: boolean;
  servers: GatewayServer[];
  /**
   * The gateway's OWN icon (its card in the Add-Account gateway list), reported by the gateway
   * itself — independent of the company / server logos inside it. Absent = generic gateway glyph.
   */
  logoUrl?: string | null;
}

/** One reachable host:port for a broker server. A server may expose several. */
export interface GatewayEndpoint {
  host: string;
  port: number;
}

/** A broker server under a company, with one or more connectable endpoints. */
export interface GatewayServerNode {
  name: string;
  logoUrl?: string | null;
  site?: string | null;
  accountsHint?: number | null;
  endpoints: GatewayEndpoint[];
}

/** A broker/company grouping one or more servers — the tree `GET /api/companies` returns. */
export interface GatewayCompany {
  company: string;
  logoUrl?: string | null;
  site?: string | null;
  servers: GatewayServerNode[];
}

/**
 * A user-defined gateway endpoint (Desk plan perk). Terminal-internal data:
 * persisted in localStorage only, never synced to any server. Its
 * `gatewaySlug` convention is `custom:<id>`.
 */
export interface CustomGateway {
  id: string;
  name: string;
  /** Reachable SignalR base URL for this gateway, e.g. http://localhost:5031. */
  hubUrl: string;
  /** Trade host of the broker/engine endpoint. */
  host: string;
  /** Trade port of the broker/engine endpoint. */
  port: number;
  custom: true;
}

/** Slug prefix reserved for custom (localStorage-only) gateways. */
export const CUSTOM_GATEWAY_SLUG_PREFIX = "custom:";

export interface GatewaysStore {
  gateways: Gateway[];
  /** Desk-only custom gateways — localStorage-backed, never server-synced. */
  customGateways: CustomGateway[];
  isLoading: boolean;
  error: string | null;
  load: () => Promise<void>;
  getBySlug: (slug: string) => Gateway | undefined;
  addCustom: (gateway: Omit<CustomGateway, "id" | "custom">) => CustomGateway;
  removeCustom: (id: string) => void;
}
